'use client';

import type { commerceJWT, loginResponse } from '@repo/ecommerce-api';
import { DevOptionSelect, DevPanel as DevPanelShell, DevSectionTitle } from '@repo/ui';
import { parseJwt, setClientCookie } from '@repo/utils';
import { useRouter } from 'next/navigation';
import {
  CREATE_ORDER_SCENARIOS,
  DEADLINE_SCENARIOS,
  DEFAULT_MOCK_CONFIG,
  DNI_SCENARIOS,
  LIST_SIZES,
  LISTS,
  ORDER_STATUS_SCENARIOS,
  parseMockConfig,
  POLICY_SCENARIOS,
  RATING_SCENARIOS,
  SMS_SCENARIOS,
} from './config';

/**
 * Commerce dev tools: on top of the shared panel, how many rows each list holds and the outcome of every step of a
 * new sale, from the sale types to the order's final status.
 */
export const DevPanel = () => {
  const router = useRouter();

  return (
    <DevPanelShell
      envForcesMock={process.env.NEXT_PUBLIC_ECOMMERCE_API_FORCEMOCK === 'true'}
      envFlagName="NEXT_PUBLIC_ECOMMERCE_API_FORCEMOCK"
      defaults={DEFAULT_MOCK_CONFIG}
      parse={parseMockConfig}
      failureHelper="Aplica a todos los pedidos al mock: listados, nueva venta y detalle de la orden."
      renderScenarios={({ config, update, disabled }) => (
        <>
          <DevSectionTitle>Listados</DevSectionTitle>
          {LISTS.map(({ id, label }) => (
            <DevOptionSelect
              key={id}
              label={label}
              value={config[id]}
              options={LIST_SIZES}
              onChange={(size) => update({ [id]: size })}
              disabled={disabled}
            />
          ))}
          <DevSectionTitle>Nueva venta</DevSectionTitle>
          <DevOptionSelect
            label="Tipos de venta"
            value={config.policies}
            options={POLICY_SCENARIOS}
            onChange={(policies) => update({ policies })}
            disabled={disabled}
          />
          <DevOptionSelect
            label="Búsqueda por DNI"
            value={config.dniNames}
            options={DNI_SCENARIOS}
            onChange={(dniNames) => update({ dniNames })}
            disabled={disabled}
          />
          <DevOptionSelect
            label="Evaluación del cliente"
            value={config.rating}
            options={RATING_SCENARIOS}
            onChange={(rating) => update({ rating })}
            helper="Rechazado lleva a Sin oferta; plan inválido, a su propia pantalla."
            disabled={disabled}
          />
          <DevOptionSelect
            label="Planes de cuotas"
            value={config.deadlines}
            options={DEADLINE_SCENARIOS}
            onChange={(deadlines) => update({ deadlines })}
            disabled={disabled}
          />
          <DevOptionSelect
            label="Creación de la orden"
            value={config.createOrder}
            options={CREATE_ORDER_SCENARIOS}
            onChange={(createOrder) => update({ createOrder })}
            disabled={disabled}
          />
          <DevSectionTitle>Detalle de la orden</DevSectionTitle>
          <DevOptionSelect
            label="Estado de la orden"
            value={config.orderStatus}
            options={ORDER_STATUS_SCENARIOS}
            onChange={(orderStatus) => update({ orderStatus })}
            helper="La pantalla lo vuelve a consultar cada 10 s: cambialo con la orden abierta para ver la transición."
            disabled={disabled}
          />
          <DevOptionSelect
            label="Envío por SMS y WhatsApp"
            value={config.sms}
            options={SMS_SCENARIOS}
            onChange={(sms) => update({ sms })}
            disabled={disabled}
          />
        </>
      )}
      // Signs in with the mock login's token and the same cookies the login page writes, then lands where it does
      mockLogin={{
        tokenCookie: 'token-commerce',
        login: async () => {
          const { token } = (await (await fetch('/mock/api/login', { method: 'POST' })).json()) as loginResponse;
          const { role } = parseJwt(token) as commerceJWT;
          // Same 12 h the login page gives a mock session
          setClientCookie('token-commerce', token, new Date(Date.now() + 12 * 60 * 60 * 1000).toUTCString());
          setClientCookie('role-commerce', role);
          router.push(role === 'ADMIN' ? '/orders' : '/new_order');
        },
      }}
      // Phones: left, above the bottom tab bar (the WhatsApp button sits on the right). Desktop: right, as the side
      // menu covers the left edge, above the WhatsApp and Sendpulse chat buttons
      fabSx={{ left: { xs: 16, md: 'auto' }, right: { md: 24 }, bottom: { xs: 96, md: 104 } }}
    />
  );
};
